import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type SelectionPanelProps } from '../SelectionPanel/SelectionPanel.types';
import { useMultiSelectionPanel } from '../useMultiSelectionPanel/useMultiSelectionPanel';
import { useSelectionFilter } from '../useSelectionFilter/useSelectionFilter';
import { useSelectionKeyboard } from '../useSelectionKeyboard/useSelectionKeyboard';
import { useSelectionValue } from '../useSelectionValue/useSelectionValue';

import { type MultiSelectProps } from './MultiSelect.types';

export type UseMultiSelectLogicInput = Omit<
  MultiSelectProps,
  'id' | 'className' | 'size' | 'placeholder' | 'aria-describedby' | 'aria-labelledby'
>;

export interface UseMultiSelectLogic {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  panel: Omit<SelectionPanelProps, 'mode'>;
  /** Chips: the chosen values, labelled. */
  labelOf: (value: string) => string;
  isClearVisible: boolean;
  clear: () => void;
  removeChip: (value: string) => void;
  summary: string;
}

/**
 * `MultiSelect`'s own wiring: the filter it owns, the create affordance, and
 * open/close. The selection algebra comes from `useSelectionValue` and the panel
 * assembly from `useMultiSelectionPanel`, both shared with `ServerMultiSelect`.
 */
export function useMultiSelectLogic({
  value,
  onValueChange,
  options,
  groups,
  maxSelected,
  hasSelectAll = true,
  filterFn,
  isCreatable = false,
  validateCreate,
  onCreate,
  isDisabled = false,
  isReadOnly = false,
  isLoading = false,
  isError = false,
  onRetry,
  searchPlaceholder,
  emptyLabel,
  emptyContent,
  loadingContent,
  errorContent,
  renderOption,
  isClearable = true,
}: UseMultiSelectLogicInput): UseMultiSelectLogic {
  const { t } = useTranslation('uiKit');
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState('');

  const selection = useSelectionValue({ value, onValueChange, maxSelected });

  const filtered = useSelectionFilter({
    options,
    groups,
    term,
    selected: selection.selected,
    filterFn,
    isCreatable: isCreatable && !isReadOnly,
    validateCreate,
    duplicateMessage: t('multiSelect.createDuplicate', { value: term.trim() }),
  });

  const keyboard = useSelectionKeyboard({ options: filtered.visibleOptions });

  const shared = useMultiSelectionPanel({
    value,
    selection,
    visibleOptions: filtered.visibleOptions,
    totalCount: options.length,
    maxSelected,
    hasSelectAll,
    isReadOnly,
    isNarrowed: term.trim() !== '' || filtered.hiddenCount > 0,
    isLoading,
    isError,
  });

  const byValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const labelOf = useCallback(
    (candidate: string) => byValue.get(candidate)?.label ?? candidate,
    [byValue],
  );

  const onCreateCandidate = useCallback(() => {
    const candidate = filtered.createCandidate;
    if (candidate === null || candidate.status !== 'ok') return;

    if (onCreate === undefined) {
      const outcome = selection.add(candidate.value);
      shared.announce(outcome);
      if (outcome.status !== 'changed') return;
    } else {
      onCreate(candidate.value);
    }

    shared.setAnnouncement(t('multiSelect.created', { value: candidate.value }));
    // Cleared so the next value can be typed straight away; the panel stays open
    // because adding several is one gesture, not N.
    setTerm('');
  }, [filtered.createCandidate, onCreate, selection, shared, t]);

  const onOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    // Reopening mid-search would show a list the operator did not ask for.
    if (!open) setTerm('');
  }, []);

  const panel: Omit<SelectionPanelProps, 'mode'> = {
    filter: { term, onChange: setTerm, placeholder: searchPlaceholder },
    list: {
      sections: filtered.sections,
      selected: selection.selected,
      createdValues: filtered.createdValues,
      activeValue: keyboard.activeValue,
      isOptionDisabled: shared.isOptionDisabled,
      isReadOnly,
      onSelect: shared.onSelect,
      renderOption,
    },
    selectAll: shared.selectAll,
    create:
      isCreatable && !isReadOnly
        ? {
            candidate: filtered.createCandidate,
            onCreate: onCreateCandidate,
            isDisabled: selection.isAtLimit,
          }
        : undefined,
    status: {
      isLoading,
      isError,
      onRetry,
      hiddenCount: filtered.hiddenCount,
      isEmpty: filtered.isEmpty,
      emptyLabel,
      emptyContent,
      loadingContent,
      errorContent,
    },
    footer: shared.footer,
    announcement: shared.announcement,
    listRef: keyboard.listRef,
    onKeyDown: keyboard.onKeyDown,
  };

  return {
    isOpen,
    onOpenChange,
    panel,
    labelOf,
    isClearVisible: isClearable && value.length > 0 && !isDisabled && !isReadOnly,
    clear: selection.clear,
    removeChip: shared.onSelect,
    summary: shared.summary,
  };
}
